import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import config from '../../../config';

// HR Departments master — minimal CRUD page. List + add/edit dialog +
// soft delete via the backend's DELETE endpoint. Same shape will be
// reused for Designations and other simple masters in the HR module.
export default function Departments() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new
  const [form, setForm] = useState({ name: '', description: '', isActive: true });
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Departments/${companyId}?includeInactive=${includeInactive}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed');
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load departments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, includeInactive]);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '', isActive: true });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.departmentName || '',
      description: row.description || '',
      isActive: !!row.isActive,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast('Please enter a department name', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: editing?.id || null,
        CompanyId: companyId,
        DepartmentName: form.name.trim(),
        Description: form.description || null,
        IsActive: form.isActive,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Department`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.message || 'Save failed', 'error');
        return;
      }
      toast(editing ? 'Department updated' : 'Department added');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.departmentName}"?`)) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Department/${row.id}/${companyId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error();
      toast('Department deleted');
      load();
    } catch (e) {
      toast('Delete failed', 'error');
    }
  };

  const filtered = rows.filter((r) =>
    r.departmentName?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        sx={{ mb: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#1A56DB',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <GroupsIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Departments
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR / Staff master · {filtered.length} record(s)
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5}>
          <TextField
            size='small'
            placeholder='Search…'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 220 }}
          />
          <Stack direction='row' alignItems='center'>
            <Switch
              size='small'
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              Show inactive
            </Typography>
          </Stack>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ bgcolor: '#1A56DB', '&:hover': { bgcolor: '#1646b0' } }}>
            Add Department
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(15,23,42,0.06)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employees</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No departments found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filtered.map((r, i) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.departmentName}
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>
                        {r.description || '—'}
                      </TableCell>
                      <TableCell>{r.employeeCount}</TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.isActive ? 'Active' : 'Inactive'}
                          sx={{
                            bgcolor: r.isActive ? '#dcfce7' : '#fee2e2',
                            color: r.isActive ? '#166534' : '#991b1b',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => openEdit(r)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Department' : 'New Department'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label='Department Name *'
              size='small'
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label='Description'
              size='small'
              fullWidth
              multiline
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
            <Stack direction='row' alignItems='center' spacing={1}>
              <Switch
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              <Typography sx={{ fontSize: '0.85rem' }}>
                {form.isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#1A56DB', '&:hover': { bgcolor: '#1646b0' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
